// delegated-g3-approve-scope-guard.ts — 06k C7 (P2): "Bảng G3 ủy nhiệm" thành luật
// máy. Một nguồn cho lệnh `approve g3 --delegated` (trước khi ghi directive) và
// cho xử lý directive (C7b, delegated-g3-directive-scope-check.ts).
//
//   deploy_recipe | deploy_target                         | approve g3 --delegated
//   có            | vắng/không đọc được/đổi sau G0         | dừng (exit 1), báo chủ mission
//   có            | ≠ release.deploy_environment (hoặc vắng) | dừng — báo key `release.deploy_environment`
//   có            | staging/production, không opt-in       | dừng — báo key `release.allow_delegated_g3_above_qa`
//   có            | còn lại                               | BẮT BUỘC --targets = [target_branch] + --expires ∈ (now, now+1h]
//   vắng          | bất kỳ                                | --targets/--expires CẤM; duyệt không phạm vi
//   config.release vắng ⇒ không có G3 ⇒ mọi approve g3 --delegated bị từ chối.
//
// "Còn nguyên từ G0" (N12/N24, F-05): mission-deploy-target.json đọc được,
// `declared_at` ≤ `first_approved_at` của G0 (lần duyệt G0 ĐẦU — gates.json; journal
// không mang mốc thời gian) và journal có `gate_approved{g0}`. Lý do từ chối nêu
// đúng tên key config để pack báo Luke (hợp đồng chat-drive.md).
import { readFile } from "node:fs/promises";
import { join } from "node:path";
import { loadEffectiveConfig } from "./effective-config-loader.js";
import { readGateFile } from "./gate-registry.js";
import { scanJournal } from "./journal-scan-report.js";
import { readMissionDeployTarget } from "./mission-deploy-target.js";
import { MISSION_JOURNAL_FILE_NAME } from "./mission-store.js";
import type { ReleaseConfig } from "./release-manifest-hash.js";
import type { DeployTarget } from "./schemas/init-schema.js";

const MAX_DELEGATED_G3_TTL_MS = 60 * 60 * 1000;

export interface DelegatedG3ScopeInput {
  targets: readonly string[] | undefined;
  expires: string | undefined;
  release: ReleaseConfig | undefined;
  /** deploy_target còn nguyên từ G0 đầu (`intactDeployTargetSinceG0`); `undefined` = không. */
  deployTarget: DeployTarget | undefined;
  now: Date;
}

export type DelegatedG3ScopeVerdict = { ok: true } | { ok: false; reason: string };

const reject = (reason: string): DelegatedG3ScopeVerdict => ({ ok: false, reason });

export function evaluateDelegatedG3Scope(input: DelegatedG3ScopeInput): DelegatedG3ScopeVerdict {
  const { release, targets, expires } = input;
  if (release === undefined) {
    return reject("config.release vắng — mission không có G3; không duyệt G3 ủy nhiệm.");
  }
  if (release.deploy_recipe === undefined) {
    return targets !== undefined || expires !== undefined
      ? reject("config.release không có deploy_recipe ⇒ G3 ủy nhiệm cấm --targets/--expires.")
      : { ok: true };
  }
  const branch = release.target_branch;
  if (targets === undefined || targets.length !== 1 || targets[0] !== branch) {
    return reject(
      `G3 ủy nhiệm: --targets phải đúng [${branch}] (target_branch của config.release), ` +
        `nhận ${JSON.stringify(targets ?? [])}.`,
    );
  }
  const expiresMs = expires === undefined ? Number.NaN : Date.parse(expires);
  const nowMs = input.now.getTime();
  if (
    !Number.isFinite(expiresMs) ||
    expiresMs <= nowMs ||
    expiresMs > nowMs + MAX_DELEGATED_G3_TTL_MS
  ) {
    return reject("G3 ủy nhiệm: --expires phải là ISO 8601 trong (bây giờ, bây giờ + 1 giờ].");
  }
  const target = input.deployTarget;
  if (target === undefined) {
    return reject(
      "G3 ủy nhiệm: deploy_target vắng, không đọc được hoặc đổi sau G0 — dừng, báo chủ mission.",
    );
  }
  if (release.deploy_environment !== target) {
    return reject(
      `G3 ủy nhiệm: release.deploy_environment (${release.deploy_environment ?? "vắng"}) ` +
        `≠ deploy_target ${target} — dừng, báo chủ mission.`,
    );
  }
  if ((target === "staging" || target === "production") && !release.allow_delegated_g3_above_qa) {
    return reject(
      `G3 ủy nhiệm: deploy_target ${target} cần release.allow_delegated_g3_above_qa: true — dừng, báo chủ mission.`,
    );
  }
  return { ok: true };
}

/** N12/N24 (F-05): deploy_target nếu đọc được ∧ khai trước lần duyệt G0 ĐẦU ∧ journal có G0. */
export async function intactDeployTargetSinceG0(
  controlRoot: string,
): Promise<DeployTarget | undefined> {
  try {
    const target = await readMissionDeployTarget(controlRoot);
    if (target === undefined) return undefined;
    const raw = await readFile(join(controlRoot, "mission-deploy-target.json"), "utf8");
    const declaredAt = Date.parse(
      String((JSON.parse(raw) as { declared_at?: unknown }).declared_at),
    );
    const g0 = (await readGateFile(controlRoot)).g0;
    if (g0?.status !== "approved" || g0.first_approved_at === undefined) return undefined;
    const scan = await scanJournal(join(controlRoot, MISSION_JOURNAL_FILE_NAME));
    const g0Journaled = scan.records.some(
      (r) => r.delta.kind === "gate_approved" && r.delta.gate === "g0",
    );
    const intact =
      g0Journaled && Number.isFinite(declaredAt) && declaredAt <= Date.parse(g0.first_approved_at);
    return intact ? target : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Kiểm cho lệnh `approve g3 --delegated` (C7): config đọc READ-ONLY (không
 * `controlRoot` ⇒ không ghi snapshot — N13). Trả lý do từ chối hoặc `undefined`.
 */
export function delegatedG3ApproveGuard(
  globalConfigPath: string | undefined,
  scope: { targets: string[] | undefined; expires: string | undefined },
  now: () => Date = () => new Date(),
): (mission: { controlRoot: string }) => Promise<string | undefined> {
  return async ({ controlRoot }) => {
    if (globalConfigPath === undefined) return "G3 ủy nhiệm: không biết đường config global.";
    const loaded = await loadEffectiveConfig({ globalConfigPath });
    if (!loaded.ok) return `G3 ủy nhiệm: config global không hợp lệ (${loaded.message}).`;
    const verdict = evaluateDelegatedG3Scope({
      ...scope,
      release: loaded.config.release,
      deployTarget: await intactDeployTargetSinceG0(controlRoot),
      now: now(),
    });
    return verdict.ok ? undefined : verdict.reason;
  };
}
